import React, { useMemo } from 'react'

import DropDownMenu from '@/common/components/DropDownMenu'
import DropDownRange from '@/common/components/DropDownRange'
import Gap from '@/common/components/Gap'
import Table, { TableProps } from '@/common/components/Table'
import useI18n from '@/common/hooks/useI18n'
import { ACTION, SEE_DETAILS } from '@/common/translation'
import { numberToString } from '@/common/utils/string'
import {
  BUDGET_MIN,
  BUDGET_MAX,
  BUDGET_STEP,
  NUMBER_OF_BEDROOM_OPTIONS,
} from '@/modules/me/pages/AdvancedSearchHistoryPage/constants'
import {
  WORKPLACE,
  RENTAL_BUDGET,
  BEDROOM,
} from '@/modules/me/pages/AdvancedSearchHistoryPage/translation'

import { SeeDetailsButton } from './styled'

const DROP_DOWN_PROPS = { labelClassName: 'text-12 text-medium text-nowrap' }

interface AdvancedSearchHistoryTableProps
  extends Omit<TableProps<RentAPerfect.AdvancedSearchHistory>, 'columns'> {
  filter: {
    budget: [number, number]
    bedroom?: number
  }
  onFilterChange: (key: string) => (value: any) => void
  getDetailsUrl: (search: RentAPerfect.AdvancedSearchHistory) => string
}

const AdvancedSearchHistoryTable = ({
  filter,
  onFilterChange,
  getDetailsUrl,
  ...props
}: AdvancedSearchHistoryTableProps) => {
  const { t } = useI18n()

  const columns: TableProps['columns'] = useMemo(
    () => [
      {
        id: 'workplace',
        Header: () => (
          <div className='text-12 text-semibold text-left'>{t(WORKPLACE)}</div>
        ),
        accessor: ({ workPlace }: RentAPerfect.AdvancedSearchHistory) => {
          return <div className='text-12 text-medium'>{workPlace ?? '-'}</div>
        },
      },
      {
        id: 'budget',
        style: { width: 0 },
        Header: () => (
          <Gap
            className='align-start width-fit-content mx-auto'
            space={4}
            vertical
          >
            <div className='text-12 text-semibold text-nowrap'>
              {t(RENTAL_BUDGET)}
            </div>
            <DropDownRange
              value={filter.budget}
              onChange={onFilterChange('budget')}
              prefixLabel='฿'
              min={BUDGET_MIN}
              max={BUDGET_MAX}
              step={BUDGET_STEP}
              dropDownProps={DROP_DOWN_PROPS}
            />
          </Gap>
        ),
        accessor: ({
          minPrice,
          maxPrice,
        }: RentAPerfect.AdvancedSearchHistory) => (
          <div className='text-center text-12 text-medium text-nowrap'>
            ฿{numberToString(minPrice)} - ฿{numberToString(maxPrice)}
          </div>
        ),
      },
      {
        id: 'bedroom',
        style: { width: 0 },
        Header: () => (
          <Gap className='align-start' space={4} vertical>
            <div className='text-12 text-semibold text-nowrap'>
              {t(BEDROOM)}
            </div>
            <DropDownMenu
              value={filter.bedroom}
              options={NUMBER_OF_BEDROOM_OPTIONS}
              onChange={onFilterChange('bedroom')}
              dropDownProps={DROP_DOWN_PROPS}
            />
          </Gap>
        ),
        accessor: ({ bedroom }: RentAPerfect.AdvancedSearchHistory) => (
          <div className='text-center text-12 text-medium'>
            {numberToString(bedroom)}
          </div>
        ),
      },
      {
        id: 'action',
        style: { width: 0 },
        Header: () => (
          <div className='text-12 text-semibold text-nowrap'>{t(ACTION)}</div>
        ),
        accessor: (history: RentAPerfect.AdvancedSearchHistory) => (
          <SeeDetailsButton to={getDetailsUrl(history)}>
            {t(SEE_DETAILS)}
          </SeeDetailsButton>
        ),
      },
    ],
    [t, filter, onFilterChange, getDetailsUrl]
  )

  return <Table {...props} columns={columns} />
}

export default AdvancedSearchHistoryTable
